import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Search, TrendingUp } from "lucide-react";
import { fmtTL, fmtDate, fmtNum } from "@/lib/utils";

const tip = { background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8 };

export default function PriceTrend({ item, onItem }) {
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (q.trim().length < 2) { setOpts([]); return; }
    const t = setTimeout(() => api.get("/analytics/price-reference", { params: { q } }).then(r => setOpts(r.data)), 300);
    return () => clearTimeout(t);
  }, [q]);
  useEffect(() => {
    if (!item) return;
    api.get("/analytics/price-trend", { params: { description: item.description, unit: item.unit } }).then(r => setData(r.data));
  }, [item]);

  const pick = (o) => { onItem({ description: o.description, unit: o.unit }); setQ(""); setOpts([]); };

  return (
    <div className="rounded-xl border border-border bg-card p-5 mt-6" data-testid="price-trend-panel">
      <h3 className="font-display font-semibold mb-4 flex items-center gap-2"><TrendingUp className="w-4 h-4 text-amber-500" /> Birim Fiyat Trendi</h3>
      <div className="relative max-w-xl mb-4">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9" placeholder="İş kalemi seçin (örn: kaba sıva, strafor)" value={q} onChange={e => setQ(e.target.value)} data-testid="trend-search" />
        {opts.length > 0 && (
          <div className="absolute z-50 mt-1 w-full max-h-64 overflow-y-auto rounded-lg border border-border bg-popover shadow-lg">
            {opts.map((o, i) => (
              <button key={i} type="button" onClick={() => pick(o)} className="w-full text-left px-3 py-2 text-xs hover:bg-amber-500/10 border-b border-border last:border-0" data-testid="trend-option">
                <div className="font-medium">{o.description}</div>
                <div className="text-muted-foreground">{o.unit} · ort. {fmtTL(o.avg)} · {o.count} fiyat</div>
              </button>
            ))}
          </div>
        )}
      </div>
      {!item && <p className="text-sm text-muted-foreground">Bir kalem arayın veya yukarıdaki birim fiyat tablosunda bir satıra tıklayın.</p>}
      {item && data && (
        <div data-testid="trend-result">
          <div className="text-sm mb-3"><strong>{item.description}</strong> <span className="text-muted-foreground">· {item.unit}{data.keys > 1 ? ` · ${data.keys} eşlenmiş yazım dahil` : ""}</span></div>
          {data.years.length === 0 ? <p className="text-sm text-muted-foreground">Bu kalem için fiyat verisi yok.</p> : (
            <div className="grid lg:grid-cols-[1.4fr_1fr] gap-6">
              <ResponsiveContainer width="100%" height={260}>
                <LineChart data={data.years}>
                  <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                  <XAxis dataKey="year" tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" />
                  <YAxis tickFormatter={v => fmtNum(v)} tick={{ fontSize: 11 }} stroke="hsl(var(--muted-foreground))" width={70} />
                  <Tooltip formatter={v => fmtTL(v)} contentStyle={tip} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Line type="monotone" dataKey="max" name="En yüksek" stroke="#e11d48" strokeDasharray="4 4" dot={{ r: 3 }} />
                  <Line type="monotone" dataKey="avg" name="Ortalama" stroke="#f59e0b" strokeWidth={3} dot={{ r: 4 }} />
                  <Line type="monotone" dataKey="min" name="En düşük" stroke="#059669" strokeDasharray="4 4" dot={{ r: 3 }} />
                </LineChart>
              </ResponsiveContainer>
              <Table>
                <TableHeader><TableRow><TableHead>Yıl</TableHead><TableHead className="text-right">Ortalama</TableHead><TableHead className="text-right">Min–Max</TableHead><TableHead className="text-right">Değişim</TableHead></TableRow></TableHeader>
                <TableBody>{data.years.map(y => (
                  <TableRow key={y.year} data-testid={`trend-year-${y.year}`}>
                    <TableCell className="font-mono-nums font-semibold">{y.year}</TableCell>
                    <TableCell className="text-right font-mono-nums">{fmtTL(y.avg)}</TableCell>
                    <TableCell className="text-right font-mono-nums text-xs">{fmtTL(y.min)} – {fmtTL(y.max)}</TableCell>
                    <TableCell className={`text-right font-mono-nums text-xs font-semibold ${y.change_pct > 0 ? "text-rose-600" : y.change_pct < 0 ? "text-emerald-600" : "text-muted-foreground"}`}>
                      {y.change_pct == null ? "—" : `${y.change_pct > 0 ? "+" : y.change_pct < 0 ? "-" : ""}%${fmtNum(Math.abs(y.change_pct))}`}
                    </TableCell>
                  </TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
          {data.points.length > 0 && (
            <div className="mt-4 max-h-64 overflow-y-auto rounded-lg border border-border">
              <Table>
                <TableHeader><TableRow className="bg-muted/40"><TableHead>Firma</TableHead><TableHead>Proje</TableHead><TableHead className="text-right">Birim Fiyat</TableHead><TableHead className="text-right">Miktar</TableHead><TableHead>Dönem</TableHead></TableRow></TableHeader>
                <TableBody>{data.points.map((p, i) => (
                  <TableRow key={i}><TableCell className="text-xs">{p.firm}</TableCell><TableCell className="text-xs">{p.project}</TableCell>
                    <TableCell className="text-right font-mono-nums font-semibold">{fmtTL(p.unit_price)}</TableCell><TableCell className="text-right font-mono-nums text-xs">{fmtNum(p.qty)}</TableCell>
                    <TableCell className="text-xs text-muted-foreground">{fmtDate(p.first)} — {fmtDate(p.last)}</TableCell></TableRow>
                ))}</TableBody>
              </Table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
